import { SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import { JobStateEvent } from '@treadinc/horizon-api-spec';
import { useCallback, useContext, useState } from 'react';

import JobPausedIndicator from '~components/Job/JobPausedIndicator';
import {
  isJobAssignmentCancelAction,
  trackJobAssignmentCancelled,
} from '~hooks/useAnalytics/trackJobAssignmentCancelled';
import { JobInvoicing } from '~hooks/useJob/models';
import { useJob } from '~hooks/useJob/useJob';
import JobStatusTransitioner from '~pages/Dispatch/components/JobStatusTransitioner';
import { getJobNextEvents } from '~pages/Dispatch/utils';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import JobsQueryContext from '../../OrdersList/ExpandedBlock/JobsQueryContext';

interface StatusColumnProps {
  job: JobInvoicing;
  onJobUpdated?: (job: JobInvoicing) => void;
  sx?: SxProps<Theme>;
}

export default function StatusColumn({ job, onJobUpdated, sx }: StatusColumnProps) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { canEditJob } = userStore.getPermissions();

  const { updateJobInQueryData } = useContext(JobsQueryContext);

  const { doEvent } = useJob();

  const [isTransitioningToStatus, setIsTransitioningToStatus] = useState<JobStateEvent>();

  const isPaused = Boolean(job.pausedAt);
  const transitionableStates = canEditJob ? getJobNextEvents(job) : [];

  const handleTransitionToStatus = useCallback(
    async (jobEvent: JobStateEvent) => {
      setIsTransitioningToStatus(jobEvent);

      try {
        const updatedJob = await doEvent(job.id, jobEvent, companyId);

        if (isJobAssignmentCancelAction(jobEvent)) {
          trackJobAssignmentCancelled({
            action: jobEvent,
            jobIds: [job.id],
            source: 'approvals_status_menu',
          });
        }

        if (updatedJob) {
          // Transition endpoints omit invoicing data — re-attach the row's invoices.
          const updatedJobWithInvoices = JobInvoicing.fromJobOrJobInvoicingAndInvoices(
            updatedJob,
            job.receivableInvoice,
            job.payableInvoice,
          );

          if (onJobUpdated) {
            onJobUpdated(updatedJobWithInvoices);
          } else {
            updateJobInQueryData(updatedJobWithInvoices);
          }
        }
      } catch {
        // doEvent already surfaced the failure (toast + Sentry) via handleRequestError.
      } finally {
        setIsTransitioningToStatus(undefined);
      }
    },
    [
      job.id,
      job.receivableInvoice,
      job.payableInvoice,
      companyId,
      onJobUpdated,
      doEvent,
      updateJobInQueryData,
    ],
  );

  return (
    <Box onClick={(event) => event.stopPropagation()} sx={sx}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {isPaused && <JobPausedIndicator />}

        <JobStatusTransitioner
          closeOnTransitionComplete
          isTransitioningToStatus={isTransitioningToStatus}
          job={job}
          onTransitionToStatus={handleTransitionToStatus}
          transitionableStates={transitionableStates}
        />
      </Box>
    </Box>
  );
}
